// Étape « Mémoire technique » du dossier : liste des mémoires, état des lieux (ce que le mémoire saura dire, ce qui manque),
// choix du format et des sections, saisie propre au chantier, génération. Un compte qui ne rédige pas voit les mémoires émis.
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { FileText, Wand2 } from 'lucide-react';
import { api, messageErreur } from '../../lib/api.ts';
import { date, pluriel } from '../../lib/format.ts';
import { useSession } from '../../lib/session.tsx';
import { Alerte, Badge, Bouton, Champ, ChampChoix, ChampTexte, Chargement, EtatErreur, EtatVide, Panneau, useNotifier } from '../../components/ui.tsx';
import { DossierCadre } from '../etudes/DossierCadre.tsx';
import { AIDES_FORMAT, LIBELLES_FORMAT, STATUTS, type Diagnostic, type FormatMemoire, type MemoireListe } from './commun.ts';

export function PageMemoires() {
  const { id = '' } = useParams();
  const liste = useQuery({ queryKey: ['memoires', id], queryFn: () => api.get<{ memoires: MemoireListe[]; modifiable: boolean }>(`/api/dossiers/${id}/memoires`) });
  const memoires = liste.data?.memoires ?? [];
  const enCours = memoires.find((x) => x.statut !== 'emis') ?? memoires[0];
  // Action principale visible sans défiler : ouvrir le mémoire en cours, sinon aller au formulaire de préparation.
  const actions = enCours
    ? <Link className="btn btn-principal" to={`/etudes/${id}/memoires/${enCours.id}`}><FileText aria-hidden />{enCours.statut === 'emis' ? `Ouvrir ${enCours.numero ?? 'le mémoire'}` : 'Ouvrir le mémoire en cours'}</Link>
    : liste.data?.modifiable ? <a className="btn btn-principal" href="#me-nouveau"><Wand2 aria-hidden />Préparer le mémoire</a> : undefined;
  return (
    <DossierCadre dossierId={id} actif="memoire" actions={actions}>
      {liste.isLoading ? <Chargement lignes={4} /> : liste.isError || !liste.data ? <EtatErreur erreur={liste.error} reessayer={() => liste.refetch()} /> : (
        <div className="me-liste-page pile-l">
          <ListeMemoires dossierId={id} memoires={memoires} redacteur={liste.data.modifiable} />
          {liste.data.modifiable && (memoires.length
            ? <details className="me-autre"><summary className="btn">Préparer un autre mémoire</summary><NouveauMemoire dossierId={id} /></details>
            : <NouveauMemoire dossierId={id} />)}
        </div>
      )}
    </DossierCadre>
  );
}

function ListeMemoires({ dossierId, memoires, redacteur }: { dossierId: string; memoires: MemoireListe[]; redacteur: boolean }) {
  if (!memoires.length) {
    return (
      <Panneau titre="Mémoires techniques du dossier">
        <EtatVide icone={<FileText aria-hidden />} titre="Aucun mémoire pour ce dossier"
          texte={redacteur ? 'Préparez-le ci-dessous : il est assemblé à partir du devis, du CCTP analysé, du périmètre et de la fiche entreprise.' : 'Aucun mémoire émis pour ce dossier.'} />
      </Panneau>
    );
  }
  return (
    <Panneau titre="Mémoires techniques du dossier">
      <ul className="me-liste">
        {memoires.map((x) => (
          <li key={x.id}>
            <Link to={`/etudes/${dossierId}/memoires/${x.id}`} className="me-liste-lien">
              <span className="me-liste-titre">{x.numero ?? 'Mémoire en préparation'}{x.version_no > 1 ? ` — version ${x.version_no}` : ''}</span>
              <span className="rangee" style={{ gap: 6, flexWrap: 'wrap' }}>
                <Badge ton={STATUTS[x.statut].ton}>{STATUTS[x.statut].libelle}</Badge>
                <Badge ton="neutre" icone={false}>{LIBELLES_FORMAT[x.format]}</Badge>
                {x.version_document === 'travail' && <Badge ton="ambre" icone={false}>Version de travail</Badge>}
                <span className="petit secondaire">{x.emis_le ? `émis le ${date(x.emis_le)}` : `modifié le ${date(x.modifie_le)}`}</span>
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </Panneau>
  );
}

const PHASES: Record<string, string> = { appel_offres: 'Appel d’offres (remise de l’offre)', marche_signe: 'Marché signé', execution: 'Exécution' };
const MARCHES: Record<string, string> = { prive: 'Marché privé (architecte, maître d’œuvre)', public: 'Marché public' };
const opts = (o: Record<string, string>) => Object.entries(o).map(([valeur, libelle]) => ({ valeur, libelle }));

function NouveauMemoire({ dossierId }: { dossierId: string }) {
  const q = useQuery({ queryKey: ['memoire-diagnostic', dossierId], queryFn: () => api.get<Diagnostic>(`/api/dossiers/${dossierId}/memoires/diagnostic`) });
  if (q.isLoading) return <Chargement lignes={5} libelle="État des lieux du dossier…" />;
  if (q.isError || !q.data) return <EtatErreur erreur={q.error} reessayer={() => q.refetch()} />;
  return <Formulaire dossierId={dossierId} diag={q.data} />;
}

function Formulaire({ dossierId, diag }: { dossierId: string; diag: Diagnostic }) {
  const { peut } = useSession();
  const naviguer = useNavigate();
  const qc = useQueryClient();
  const notifier = useNotifier();
  const [format, setFormat] = useState<FormatMemoire>(diag.format.format);
  const [sections, setSections] = useState<string[]>(diag.sections.map((s) => s.id));
  const [s, setS] = useState<Record<string, any>>(() => ({ ...diag.saisie, contraintes: { ...(diag.saisie.contraintes ?? {}) }, sous_traitance: { ...(diag.saisie.sous_traitance ?? {}) } }));
  const [enCours, setEnCours] = useState(false);
  const champ = (k: string, v: unknown) => setS((x) => ({ ...x, [k]: v }));
  const contrainte = (k: string, v: unknown) => setS((x) => ({ ...x, contraintes: { ...x.contraintes, [k]: v } }));
  const nombre = (v: string) => (v.trim() === '' ? null : Number(v));

  const generer = async () => {
    setEnCours(true);
    try {
      const r = await api.post<{ id: string }>(`/api/dossiers/${dossierId}/memoires`, { format, sections, saisie: s });
      await qc.invalidateQueries({ queryKey: ['memoires', dossierId] });
      await qc.invalidateQueries({ queryKey: ['dossier', dossierId] });
      notifier('Mémoire préparé : relisez-le section par section.');
      naviguer(`/etudes/${dossierId}/memoires/${r.id}`);
    } catch (e) { notifier(messageErreur(e), true); } finally { setEnCours(false); }
  };

  const alertes = diag.fiche?.alertes.filter((a) => a.gravite === 'attention') ?? [];
  return (
    <div id="me-nouveau" className="me-nouveau pile-l">
      <Panneau titre="Ce que le mémoire saura dire">
        <div className="me-etat">
          <div><span className="tres-petit discret">Devis retenu</span>
            {diag.devis ? <p><strong>{diag.devis.numero}</strong>, révision {diag.devis.revision} {diag.devis.definitif ? <Badge ton="succes">émis</Badge> : <Badge ton="ambre">brouillon</Badge>}<br />
              <span className="petit secondaire">{diag.devis.lots.map((l) => `${l.titre} (${pluriel(l.postes, 'poste', 'postes')})`).join(' · ')}</span></p>
              : <p className="secondaire">Aucun devis : la méthode ne pourra pas suivre les postes.</p>}</div>
          <div><span className="tres-petit discret">Pièces techniques</span>
            <p><strong>{pluriel(diag.exigences.sourcees, 'exigence sourcée', 'exigences sourcées')}</strong> dans {pluriel(diag.pieces, 'pièce', 'pièces')}<br />
              <span className="petit secondaire">{pluriel(diag.exigences.validees, 'qualifiée', 'qualifiées')} : seules celles-ci figurent dans la réponse au CCTP.</span></p></div>
          <div><span className="tres-petit discret">Fiche entreprise</span>
            <p>{diag.fiche?.complete ? <Badge ton="succes">Complète</Badge> : <Badge ton="ambre">À compléter</Badge>}<br />
              <span className="petit secondaire">{diag.fiche ? `${diag.fiche.rubriques.filter((r) => r.etat === 'complete').length} rubriques complètes sur ${diag.fiche.rubriques.length}` : ''}</span></p></div>
        </div>
        {diag.devis && !diag.devis.definitif && <Alerte ton="info">Le devis n’est pas émis : le mémoire pourra être émis en version de travail seulement.</Alerte>}
        {alertes.length > 0 && (
          <Alerte ton="ambre" titre="Fiche entreprise" action={peut('admin.configuration') ? <Link className="btn btn-compact" to="/administration?onglet=entreprise">Ouvrir la fiche</Link> : undefined}>
            <ul className="et-liste-simple">{alertes.map((a, i) => <li key={i}>{a.message}</li>)}</ul>
            {!peut('admin.configuration') && <p className="petit">Demandez à un administrateur de la mettre à jour.</p>}
          </Alerte>
        )}
      </Panneau>

      <Panneau titre="Format">
        <fieldset className="me-formats">
          <legend className="visuellement-cache">Format du mémoire</legend>
          {diag.formats.map((f) => (
            <label key={f.id} className={`me-format ${format === f.id ? 'me-format-actif' : ''}`}>
              <input type="radio" name="format" value={f.id} checked={format === f.id} onChange={() => setFormat(f.id)} />
              <span><strong>{f.libelle}</strong>{f.id === diag.format.format && <> <Badge ton="info" icone={false}>Proposé</Badge></>}<br />
                <span className="petit secondaire">{AIDES_FORMAT[f.id]}</span></span>
            </label>
          ))}
        </fieldset>
        <p className="petit secondaire" style={{ marginTop: 8 }}>Pourquoi ce format est proposé : {diag.format.motif}</p>
      </Panneau>

      <Panneau titre="Ce qui est propre à ce chantier">
        <div className="pile">
          <p className="petit secondaire">Rien n’est deviné : un champ laissé vide apparaît « à compléter » dans le mémoire.</p>
          <div className="grille-form">
            <Champ libelle="Maître d’œuvre ou architecte" value={s.maitre_oeuvre ?? ''} onChange={(e) => champ('maitre_oeuvre', e.target.value)} />
            <ChampChoix libelle="Étape" value={s.phase ?? ''} vide="À préciser" options={opts(PHASES)} onChange={(e) => champ('phase', e.target.value || null)} />
            <ChampChoix libelle="Marché" value={s.marche ?? ''} vide="À préciser" options={opts(MARCHES)} onChange={(e) => champ('marche', e.target.value || null)} />
            <Champ libelle="Durée globale (jours ouvrés)" type="number" min={1} inputMode="numeric" value={s.duree_globale_jours ?? ''} onChange={(e) => champ('duree_globale_jours', nombre(e.target.value))} />
            <Champ libelle="Date de démarrage" type="date" value={s.date_debut ?? ''} onChange={(e) => champ('date_debut', e.target.value || null)} />
            <Champ libelle="Effectif affecté" type="number" min={0} inputMode="numeric" value={s.effectif_affecte ?? ''} onChange={(e) => champ('effectif_affecte', nombre(e.target.value))} />
            <Champ libelle="Interlocuteur unique" value={s.interlocuteur ?? ''} onChange={(e) => champ('interlocuteur', e.target.value)} />
            <Champ libelle="Encadrant du chantier" value={s.encadrant ?? ''} onChange={(e) => champ('encadrant', e.target.value)} />
          </div>
          <label className="case"><input type="checkbox" checked={!!s.contraintes?.site_occupe} onChange={(e) => contrainte('site_occupe', e.target.checked)} /><span>Site occupé pendant les travaux</span></label>
          <div className="grille-form">
            <Champ libelle="Horaires imposés" value={s.contraintes?.horaires ?? ''} onChange={(e) => contrainte('horaires', e.target.value)} />
            <Champ libelle="Accès" value={s.contraintes?.acces ?? ''} onChange={(e) => contrainte('acces', e.target.value)} />
            <Champ libelle="Stockage" value={s.contraintes?.stockage ?? ''} onChange={(e) => contrainte('stockage', e.target.value)} />
            <Champ libelle="Co-activité" value={s.contraintes?.coactivite ?? ''} onChange={(e) => contrainte('coactivite', e.target.value)} />
          </div>
          <label className="case"><input type="checkbox" checked={!!s.sous_traitance?.prevue} onChange={(e) => setS((x) => ({ ...x, sous_traitance: { ...x.sous_traitance, prevue: e.target.checked } }))} /><span>Sous-traitance prévue</span></label>
          {s.sous_traitance?.prevue && <Champ libelle="Postes sous-traités" value={s.sous_traitance?.postes ?? ''} onChange={(e) => setS((x) => ({ ...x, sous_traitance: { ...x.sous_traitance, postes: e.target.value } }))} />}
          <div className="grille-form">
            <Champ libelle="Nombre de pages maximum" aide="Fixé par le règlement de consultation, si c’est le cas" type="number" min={1} inputMode="numeric" value={s.pages_max ?? ''} onChange={(e) => champ('pages_max', nombre(e.target.value))} />
          </div>
          {format === 'cadre' && (
            <ChampTexte libelle="Rubriques imposées par l’acheteur" aide="Une rubrique par ligne, dans l’ordre du cadre de réponse" rows={5} value={s.rubriques_imposees ?? ''} onChange={(e) => champ('rubriques_imposees', e.target.value)} />
          )}
        </div>
      </Panneau>

      <Panneau titre={`Sections (${sections.length} sur ${diag.sections.length})`}>
        <ul className="me-sections-choix">
          {diag.sections.map((x) => (
            <li key={x.id}>
              <label className="case">
                <input type="checkbox" checked={sections.includes(x.id)} onChange={(e) => setSections((l) => (e.target.checked ? diag.sections.map((y) => y.id).filter((y) => y === x.id || l.includes(y)) : l.filter((y) => y !== x.id)))} />
                <span><strong>{x.numero}. {x.titre}</strong><br /><span className="petit secondaire">{x.sert_a}</span></span>
              </label>
            </li>
          ))}
        </ul>
      </Panneau>

      <div className="me-generer">
        <Bouton variante="principal" icone={<Wand2 aria-hidden />} chargement={enCours} disabled={!sections.length} onClick={generer}>Préparer le mémoire</Bouton>
        <p className="petit secondaire">Assemblé par des règles écrites, sans intelligence artificielle. Vous le relirez avant de l’émettre.</p>
      </div>
    </div>
  );
}
